import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate, STATUS_LABELS, TASK_STATUSES, initials } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { ArrowLeft, Plus, FileDown, FileText, Users2, Calendar, Wallet, ClipboardList, FolderOpen, FileEdit, Trash2, MessageSquare } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { toast } from "sonner";
import CommentThread from "../components/CommentThread";

const TABS = [
  { id: "overview", label: "Aperçu", icon: ClipboardList },
  { id: "documents", label: "Documents", icon: FolderOpen },
  { id: "deliverables", label: "Livrables", icon: FileText },
  { id: "tasks", label: "Tâches", icon: ClipboardList },
  { id: "budget", label: "Budget", icon: Wallet },
  { id: "team", label: "Équipe", icon: Users2 },
  { id: "planning", label: "Planning", icon: Calendar },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [budget, setBudget] = useState({ items: [], summary: null });
  const [forms, setForms] = useState([]);
  const [users, setUsers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [p, t, b, sum, f, u, d] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/tasks?project_id=${id}`),
        api.get(`/budget?project_id=${id}`),
        api.get(`/budget/summary/${id}`),
        api.get(`/forms?project_id=${id}`),
        api.get(`/users`),
        api.get(`/documents?project_id=${id}`),
      ]);
      setProject(p.data);
      setTasks(t.data);
      setBudget({ items: b.data, summary: sum.data });
      setForms(f.data);
      setUsers(u.data);
      setDocs(d.data);
    } catch (e) {
      toast.error("Erreur chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [id]);

  if (loading || !project) return <div className="text-slate-500">Chargement…</div>;

  const teamUsers = users.filter((u) => (project.team_ids || []).includes(u.id));

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/projects")} className="text-sm text-slate-600 hover:text-btp-navy flex items-center gap-1" data-testid="back-projects">
        <ArrowLeft size={14} /> Tous les projets
      </button>

      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-[11px] text-slate-500 px-2 py-0.5 bg-slate-100 rounded">{project.code}</span>
              <StatusBadge status={project.status} data-testid="project-status-badge" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" data-testid="project-name">{project.name}</h1>
            <div className="text-sm text-slate-500 mt-1">{project.client} · {project.country}</div>
          </div>
          <div className="flex gap-2">
            <button className="h-9 px-3 text-sm border border-slate-300 rounded-md hover:bg-slate-50 flex items-center gap-2" data-testid="assemble-final-btn"
              onClick={() => downloadAssembled(id, project.code)}>
              <FileDown size={14} /> Assembler rapport final
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden mt-6">
          <Stat label="Avancement" value={`${Math.round((project.progress || 0) * 100)}%`} mono />
          <Stat label="Budget prévu" value={fmtMoney(project.budget_prevu)} />
          <Stat label="Budget réel" value={fmtMoney(project.budget_reel)} accent={budget.summary?.alerte} />
          <Stat label="Échéance" value={fmtDate(project.end_date)} />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-2 transition-colors whitespace-nowrap ${
              tab === t.id ? "text-btp-navy border-btp-navy" : "text-slate-500 border-transparent hover:text-slate-700"
            }`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab project={project} tasks={tasks} team={teamUsers} forms={forms} users={users} />}
      {tab === "documents" && <DocumentsTab project={project} docs={docs} onChange={refresh} />}
      {tab === "deliverables" && <DeliverablesTab project={project} forms={forms} onChange={refresh} navigate={navigate} />}
      {tab === "tasks" && <TasksTab project={project} tasks={tasks} users={users} onChange={refresh} />}
      {tab === "budget" && <BudgetTab project={project} budget={budget} onChange={refresh} />}
      {tab === "team" && <TeamTab project={project} users={users} team={teamUsers} onChange={refresh} />}
      {tab === "planning" && <PlanningTab project={project} tasks={tasks} />}
    </div>
  );
}

function Stat({ label, value, mono, accent }) {
  return (
    <div className={`bg-white p-4 ${accent ? "border-l-2 border-btp-terra" : ""}`}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`mt-1.5 font-heading text-xl font-bold tracking-tight ${mono ? "font-mono" : ""} ${accent ? "text-btp-terra" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function TDRDetails({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4">
      <button onClick={() => setOpen((o) => !o)} className="text-xs text-btp-navy cursor-pointer font-semibold" data-testid="tdr-toggle">
        {open ? "Masquer le TDR" : "Voir le TDR"}
      </button>
      {open && (
        <pre className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded text-xs whitespace-pre-wrap font-mono">{text}</pre>
      )}
    </div>
  );
}

async function downloadAssembled(id, code) {
  try {
    const res = await api.get(`/projects/${id}/assemble`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `Rapport_Final_${code}.docx`; a.click();
    toast.success("Rapport assemblé");
  } catch (err) {
    toast.error(err?.response?.status === 400 ? "Aucun formulaire validé à assembler" : "Erreur");
  }
}

/* ===================== OVERVIEW ===================== */
function OverviewTab({ project, tasks, team, forms, users }) {
  const recentTasks = tasks.slice(0, 5);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3">Description</div>
          <p className="text-sm text-slate-700 leading-relaxed">{project.description || "Aucune description fournie."}</p>
          {project.tdr_text && <TDRDetails text={project.tdr_text} />}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare size={14} className="text-btp-terra" />
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Discussion projet</div>
          </div>
          <CommentThread entityType="project" entityId={project.id} users={users} />
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="font-heading text-base font-bold">Activité récente</h3>
          </div>
          <table className="btp-table">
            <thead><tr><th>Tâche</th><th>Statut</th><th>Échéance</th></tr></thead>
            <tbody>
              {recentTasks.map((t) => (
                <tr key={t.id}>
                  <td className="font-medium">{t.title}</td>
                  <td><StatusBadge status={t.status} /></td>
                  <td className="text-xs font-mono">{fmtDate(t.deadline)}</td>
                </tr>
              ))}
              {recentTasks.length === 0 && <tr><td colSpan={3} className="text-center text-slate-400 py-8">Aucune tâche</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h3 className="font-heading text-base font-bold mb-4">Équipe</h3>
          <div className="space-y-3">
            {team.length === 0 && <div className="text-sm text-slate-400">Aucun membre assigné</div>}
            {team.map((u) => (
              <div key={u.id} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-btp-navy text-white flex items-center justify-center text-xs font-semibold">{initials(u.full_name)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.expertise || u.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h3 className="font-heading text-base font-bold mb-4">Livrables ({forms.length})</h3>
          <div className="space-y-2">
            {forms.slice(0, 5).map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{f.name}</span>
                <StatusBadge status={f.status} />
              </div>
            ))}
            {forms.length === 0 && <div className="text-sm text-slate-400">Aucun livrable</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===================== DOCUMENTS ===================== */
function DocumentsTab({ project, docs, onChange }) {
  const [folder, setFolder] = useState("/");
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
        <div>
          <h3 className="font-heading text-base font-bold">Documents du projet</h3>
          <div className="text-xs text-slate-500 mt-0.5">{docs.length} fichier(s)</div>
        </div>
        <UploadDocBtn projectId={project.id} folder={folder} onUploaded={onChange} />
      </div>
      <table className="btp-table">
        <thead>
          <tr><th>Nom</th><th>Version</th><th>Statut</th><th>Maj</th><th>Action</th></tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} data-testid={`doc-row-${d.id}`}>
              <td className="font-medium">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-slate-400" /> {d.name}
                </div>
                {d.description && <div className="text-xs text-slate-500 mt-0.5">{d.description}</div>}
              </td>
              <td><span className="font-mono text-xs">v{d.current_version}</span></td>
              <td><StatusBadge status={d.status} /></td>
              <td className="text-xs font-mono text-slate-500">{fmtDate(d.updated_at)}</td>
              <td>
                <a href={`${api.defaults.baseURL}/documents/${d.id}/download/${d.current_version}`} target="_blank" rel="noreferrer"
                  onClick={(e) => downloadWithToken(e, d.id, d.current_version, d.name)}
                  className="text-btp-navy text-sm hover:underline">Télécharger</a>
              </td>
            </tr>
          ))}
          {docs.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-12">Aucun document. Téléversez le premier.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

async function downloadWithToken(e, docId, version, name) {
  e.preventDefault();
  try {
    const res = await api.get(`/documents/${docId}/download/${version}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  } catch { toast.error("Erreur téléchargement"); }
}

function UploadDocBtn({ projectId, folder, onUploaded }) {
  const [loading, setLoading] = useState(false);
  const onChange = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("project_id", projectId); fd.append("name", file.name); fd.append("folder", folder);
    fd.append("description", ""); fd.append("tags", ""); fd.append("file", file);
    try { await api.post("/documents/upload", fd); toast.success("Document téléversé"); onUploaded(); }
    catch { toast.error("Erreur upload"); }
    finally { setLoading(false); e.target.value = ""; }
  };
  return (
    <label className="bg-btp-navy hover:bg-btp-navy-hover text-white px-3 h-9 rounded-md text-sm font-medium flex items-center gap-2 cursor-pointer" data-testid="upload-doc-btn">
      <Plus size={14} /> {loading ? "Upload…" : "Téléverser"}
      <input type="file" className="hidden" onChange={onChange} disabled={loading} />
    </label>
  );
}

/* ===================== DELIVERABLES ===================== */
function DeliverablesTab({ project, forms, onChange, navigate }) {
  const create = async () => {
    try {
      const r = await api.post("/forms", {
        project_id: project.id, name: "Nouveau livrable", doc_type: "Proposition", status: "brouillon",
        sections: [{ id: crypto.randomUUID(), title: "Introduction", order: 0, type: "text", content: "" }],
      });
      navigate(`/forms/${r.data.id}`);
    } catch { toast.error("Erreur"); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-slate-500">{forms.length} livrable(s) • Cartes avec statuts visuels</div>
        <button onClick={create} className="bg-btp-terra hover:bg-btp-terra-hover text-white px-3 h-9 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="create-deliverable-btn">
          <Plus size={14} /> Nouveau livrable
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {forms.map((f) => (
          <div key={f.id} onClick={() => navigate(`/forms/${f.id}`)}
            className="bg-white border border-slate-200 rounded-md p-5 hover:border-btp-navy hover:shadow-md cursor-pointer transition-all"
            data-testid={`deliverable-card-${f.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-100 text-slate-700 rounded">{f.doc_type}</div>
              <StatusBadge status={f.status} />
            </div>
            <h4 className="font-heading font-bold text-base leading-snug mb-2">{f.name}</h4>
            <div className="text-xs text-slate-500">{f.sections?.length || 0} section(s)</div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center text-xs">
              <span className="text-slate-500">{fmtDate(f.updated_at)}</span>
              <button onClick={(e) => { e.stopPropagation(); downloadForm(f.id, f.name); }} className="text-btp-navy font-semibold hover:underline flex items-center gap-1">
                <FileDown size={12} /> Word
              </button>
            </div>
          </div>
        ))}
        {forms.length === 0 && (
          <div className="md:col-span-2 xl:col-span-3 bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
            <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Aucun livrable. Créez le premier rapport (TDR, Proposition, APS, APD, DAO).</p>
          </div>
        )}
      </div>
    </div>
  );
}

async function downloadForm(id, name) {
  try {
    const res = await api.get(`/forms/${id}/export`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${name}.docx`; a.click();
  } catch { toast.error("Erreur export"); }
}

/* ===================== TASKS ===================== */
function TasksTab({ project, tasks, users, onChange }) {
  const [showModal, setShowModal] = useState(false);
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-slate-500">{tasks.length} tâche(s)</div>
        <button onClick={() => setShowModal(true)} className="bg-btp-terra hover:bg-btp-terra-hover text-white px-3 h-9 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="add-task-btn">
          <Plus size={14} /> Ajouter tâche
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {TASK_STATUSES.map((status) => {
          const cols = tasks.filter((t) => t.status === status);
          return (
            <div key={status} className="kanban-col">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700">{STATUS_LABELS[status]}</div>
                <span className="text-xs font-mono bg-white border border-slate-200 rounded-full px-2 py-0.5">{cols.length}</span>
              </div>
              <div className="space-y-2">
                {cols.map((t) => {
                  const assignee = users.find((u) => u.id === t.assigned_to);
                  return (
                    <div key={t.id} className="bg-white border border-slate-200 rounded p-3 text-xs cursor-pointer hover:border-btp-navy" data-testid={`task-card-${t.id}`}>
                      <div className="font-medium text-slate-900 mb-2 leading-snug">{t.title}</div>
                      <div className="flex justify-between items-center">
                        <div className="text-slate-500 font-mono">{fmtDate(t.deadline)}</div>
                        {assignee && <div className="w-6 h-6 rounded-full bg-btp-navy text-white flex items-center justify-center text-[10px] font-semibold" title={assignee.full_name}>{initials(assignee.full_name)}</div>}
                      </div>
                      <select value={t.status} onChange={async (e) => { await api.patch(`/tasks/${t.id}/status?status=${e.target.value}`); onChange(); }}
                        className="mt-2 w-full text-[10px] border border-slate-200 rounded px-1 py-0.5">
                        {TASK_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {showModal && <TaskModal project={project} users={users} onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); onChange(); }} />}
    </div>
  );
}

function TaskModal({ project, users, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", assigned_to: "", priority: "moyenne", deadline: "", start_date: "", end_date: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", { project_id: project.id, ...form, status: "brouillon" });
      toast.success("Tâche créée"); onCreated();
    } catch { toast.error("Erreur"); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-lg p-6 space-y-4">
        <h2 className="font-heading text-xl font-bold">Nouvelle tâche</h2>
        <input required placeholder="Titre" value={form.title} onChange={update("title")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" data-testid="task-title-input" />
        <textarea placeholder="Description" rows={3} value={form.description} onChange={update("description")} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
        <div className="grid grid-cols-2 gap-3">
          <select value={form.assigned_to} onChange={update("assigned_to")} className="h-10 px-3 border border-slate-300 rounded text-sm">
            <option value="">Non assigné</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <select value={form.priority} onChange={update("priority")} className="h-10 px-3 border border-slate-300 rounded text-sm">
            <option value="basse">Basse</option><option value="moyenne">Moyenne</option><option value="haute">Haute</option><option value="urgente">Urgente</option>
          </select>
          <input type="date" value={form.start_date} onChange={update("start_date")} className="h-10 px-3 border border-slate-300 rounded text-sm" placeholder="Début" />
          <input type="date" value={form.end_date} onChange={update("end_date")} className="h-10 px-3 border border-slate-300 rounded text-sm" placeholder="Fin" />
          <input type="date" value={form.deadline} onChange={update("deadline")} className="h-10 px-3 border border-slate-300 rounded text-sm col-span-2" placeholder="Échéance" />
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 h-10 border border-slate-300 rounded text-sm">Annuler</button>
          <button type="submit" className="flex-1 h-10 bg-btp-terra text-white rounded text-sm font-semibold" data-testid="submit-task">Créer</button>
        </div>
      </form>
    </div>
  );
}

/* ===================== BUDGET ===================== */
function BudgetTab({ project, budget, onChange }) {
  const [showModal, setShowModal] = useState(false);
  const sum = budget.summary || {};
  const data = (sum.by_category || []).map((c) => ({
    name: c.category, prevu: c.prevu, reel: c.reel,
  }));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded overflow-hidden">
        <Stat label="Total prévu" value={fmtMoney(sum.total_lignes_prevu || 0)} />
        <Stat label="Total réel" value={fmtMoney(sum.total_lignes_reel || 0)} />
        <Stat label="Consommation" value={`${sum.consommation_pct || 0}%`} mono />
        <Stat label="Dépassement" value={fmtMoney(sum.depassement || 0)} accent={sum.alerte} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="font-heading text-base font-bold mb-1">Prévu vs Réel par catégorie</h3>
        <p className="text-xs text-slate-500 mb-4">USD ($)</p>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `${(v/1000000).toFixed(0)}M`} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="prevu" fill="#0A2540" radius={[4,4,0,0]} />
              <Bar dataKey="reel" fill="#E05A3D" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
          <h3 className="font-heading text-base font-bold">Lignes budgétaires</h3>
          <button onClick={() => setShowModal(true)} className="bg-btp-terra hover:bg-btp-terra-hover text-white px-3 h-9 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="add-budget-btn">
            <Plus size={14} /> Ajouter ligne
          </button>
        </div>
        <table className="btp-table">
          <thead><tr><th>Catégorie</th><th>Description</th><th>Prévu</th><th>Réel</th><th>Δ</th><th></th></tr></thead>
          <tbody>
            {budget.items.map((it) => {
              const delta = (it.amount_reel || 0) - (it.amount_prevu || 0);
              return (
                <tr key={it.id}>
                  <td><span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-100 rounded">{it.category}</span></td>
                  <td>{it.description}</td>
                  <td className="font-mono text-xs">{fmtMoney(it.amount_prevu)}</td>
                  <td className="font-mono text-xs">{fmtMoney(it.amount_reel)}</td>
                  <td className={`font-mono text-xs ${delta > 0 ? "text-red-600" : "text-emerald-600"}`}>{delta > 0 ? "+" : ""}{fmtMoney(delta)}</td>
                  <td>
                    <button onClick={async () => { await api.delete(`/budget/${it.id}`); toast.success("Supprimé"); onChange(); }} className="text-slate-400 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {budget.items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-8">Aucune ligne</td></tr>}
          </tbody>
        </table>
      </div>
      {showModal && <BudgetModal project={project} onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); onChange(); }} />}
    </div>
  );
}

function BudgetModal({ project, onClose, onCreated }) {
  const [form, setForm] = useState({ category: "personnel", description: "", amount_prevu: 0, amount_reel: 0 });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/budget", { project_id: project.id, ...form, amount_prevu: Number(form.amount_prevu), amount_reel: Number(form.amount_reel) });
      toast.success("Ligne ajoutée"); onCreated();
    } catch { toast.error("Erreur"); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-md p-6 space-y-4">
        <h2 className="font-heading text-xl font-bold">Nouvelle ligne budgétaire</h2>
        <select value={form.category} onChange={update("category")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm">
          <option value="personnel">Personnel</option>
          <option value="deplacement">Déplacement</option>
          <option value="materiel">Matériel</option>
          <option value="sous_traitance">Sous-traitance</option>
          <option value="autres">Autres</option>
        </select>
        <input required placeholder="Description" value={form.description} onChange={update("description")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" data-testid="budget-desc-input" />
        <div className="grid grid-cols-2 gap-3">
          <input type="number" min="0" placeholder="Montant prévu" value={form.amount_prevu} onChange={update("amount_prevu")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" data-testid="budget-prevu-input" />
          <input type="number" min="0" placeholder="Montant réel" value={form.amount_reel} onChange={update("amount_reel")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" data-testid="budget-reel-input" />
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 h-10 border border-slate-300 rounded text-sm">Annuler</button>
          <button type="submit" className="flex-1 h-10 bg-btp-terra text-white rounded text-sm font-semibold" data-testid="submit-budget">Ajouter</button>
        </div>
      </form>
    </div>
  );
}

/* ===================== TEAM ===================== */
function TeamTab({ project, users, team, onChange }) {
  const [selectedId, setSelectedId] = useState("");
  const available = users.filter((u) => !team.find((t) => t.id === u.id));

  const addMember = async () => {
    if (!selectedId) return;
    const newTeamIds = [...(project.team_ids || []), selectedId];
    try {
      await api.put(`/projects/${project.id}`, { ...project, team_ids: newTeamIds });
      toast.success("Membre ajouté"); setSelectedId(""); onChange();
    } catch { toast.error("Erreur"); }
  };
  const removeMember = async (uid) => {
    const newTeamIds = (project.team_ids || []).filter((i) => i !== uid);
    try { await api.put(`/projects/${project.id}`, { ...project, team_ids: newTeamIds }); onChange(); } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-6 flex gap-3 items-end">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1">Ajouter un membre</div>
          <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" data-testid="team-add-select">
            <option value="">— Sélectionner —</option>
            {available.map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.expertise || u.role})</option>)}
          </select>
        </div>
        <button onClick={addMember} disabled={!selectedId} className="h-10 px-4 bg-btp-terra text-white rounded text-sm font-semibold disabled:opacity-50" data-testid="team-add-btn">Ajouter</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {team.map((u) => (
          <div key={u.id} className="bg-white border border-slate-200 rounded-md p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-btp-navy text-white flex items-center justify-center font-semibold">{initials(u.full_name)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{u.full_name}</div>
              <div className="text-xs text-slate-500">{u.expertise || u.role}</div>
              <div className="text-xs text-slate-400 truncate">{u.email}</div>
            </div>
            <button onClick={() => removeMember(u.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
          </div>
        ))}
        {team.length === 0 && <div className="md:col-span-2 lg:col-span-3 text-center text-slate-400 py-12">Aucun membre dans l'équipe</div>}
      </div>
    </div>
  );
}

/* ===================== PLANNING (Gantt) ===================== */
function PlanningTab({ project, tasks }) {
  // Determine date range
  const validTasks = tasks.filter((t) => t.start_date && t.end_date);
  if (validTasks.length === 0) {
    return <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center">
      <Calendar className="w-10 h-10 mx-auto text-slate-300 mb-2" />
      <p className="text-sm text-slate-500">Aucune tâche planifiée — ajoutez dates de début/fin pour voir le Gantt.</p>
    </div>;
  }
  const minD = new Date(Math.min(...validTasks.map((t) => new Date(t.start_date).getTime())));
  const maxD = new Date(Math.max(...validTasks.map((t) => new Date(t.end_date).getTime())));
  const totalDays = Math.max(1, Math.ceil((maxD - minD) / 86400000));
  const today = new Date();

  const months = [];
  let cur = new Date(minD.getFullYear(), minD.getMonth(), 1);
  while (cur <= maxD) {
    months.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200">
        <h3 className="font-heading text-base font-bold">Planning Gantt</h3>
        <div className="text-xs text-slate-500 mt-0.5">Du {fmtDate(minD)} au {fmtDate(maxD)} • {validTasks.length} tâche(s)</div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="gantt-row !h-10 bg-slate-50">
            <div className="px-4 text-[11px] uppercase tracking-wider font-semibold text-slate-600 border-r border-slate-200 h-full flex items-center">Tâche</div>
            <div className="grid h-full" style={{ gridTemplateColumns: `repeat(${months.length}, 1fr)` }}>
              {months.map((m, i) => (
                <div key={i} className="border-r border-slate-200 h-full flex items-center px-3 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                  {m.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" })}
                </div>
              ))}
            </div>
          </div>
          {validTasks.map((t) => {
            const s = new Date(t.start_date), e = new Date(t.end_date);
            const offsetPct = ((s - minD) / 86400000) / totalDays * 100;
            const widthPct = Math.max(2, ((e - s) / 86400000) / totalDays * 100);
            const isLate = e < today && t.status !== "approuve" && t.status !== "archive";
            const isDone = t.status === "approuve" || t.status === "valide";
            return (
              <div key={t.id} className="gantt-row" data-testid={`gantt-row-${t.id}`}>
                <div className="px-4 truncate text-sm border-r border-slate-200 h-full flex items-center">
                  <span className="truncate">{t.title}</span>
                </div>
                <div className="relative h-full flex items-center px-2">
                  <div className={`gantt-bar ${isLate ? "gantt-bar-late" : isDone ? "gantt-bar-done" : ""}`}
                    style={{ marginLeft: `${offsetPct}%`, width: `${widthPct}%` }}
                    title={`${t.title} • ${fmtDate(s)} → ${fmtDate(e)}`}>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
